import React from 'react';
import { UpgradeCTA } from '../upgradelizer/UpgradeCTA';
import './ProfileMarkers.css';

/**
 * Shared rendering for scorer "profile markers" — the Strengths / Risks /
 * Suggestions columns driven by structured `{ id, data }` markers the backend
 * emits (manabase `profile.strengths|risks|improve`, power-level
 * `profile.improve`, …). Both the manabase and power-level panes render through
 * here so the marker→text mapping, unknown-id fallback, and the
 * CTA-on-suggestions-only behaviour live in exactly one place.
 */

// Fallback label for a scorer marker id with no bespoke copy:
// camelCase → "Sentence case" (e.g. "addCheckLands" → "Add check lands").
export function humanizeMarkerId(id) {
    const spaced = String(id || '')
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .trim()
        .toLowerCase();
    return spaced ? spaced.charAt(0).toUpperCase() + spaced.slice(1) : '';
}

/**
 * Map raw `{ id, data }` markers to renderable `{ id, text, data }` rows.
 * `copyMap` is the per-category copy object — `{ [id]: (data) => string }`.
 * A marker with no copy entry is NOT dropped: it degrades to a humanized text
 * label (and warns) so any new backend marker still shows.
 *
 * @param {Array} items     raw markers from the scorer
 * @param {Object} copyMap  id → (data) => string
 * @param {string} [debugLabel]  e.g. "manabase.improve" — used in warnings only
 */
export function renderProfileItems(items, copyMap, debugLabel = 'profile') {
    const map = copyMap || {};
    return (items || [])
        .map((item) => {
            const fn = map[item?.id];
            if (!fn) {
                if (item?.id && typeof console !== 'undefined' && console.warn) {
                    console.warn(`ProfileMarkers: unhandled marker ${debugLabel}.${item.id} (no client copy)`);
                }
                const label = humanizeMarkerId(item?.id);
                return label ? { id: item.id, text: label, data: item.data || {} } : null;
            }
            try {
                const text = fn(item.data || {});
                return text ? { id: item.id, text, data: item.data || {} } : null;
            } catch (err) {
                if (typeof console !== 'undefined' && console.warn) {
                    console.warn(`ProfileMarkers: failed to render ${debugLabel}.${item?.id}`, err);
                }
                return null;
            }
        })
        .filter(Boolean);
}

/**
 * One column of a profile grid — Strengths / Risks / Suggestions. Items are
 * pre-rendered (`{ id, text, data }`) by `renderProfileItems`.
 *
 * Only the Suggestions column (tone 'flat') is actionable: when `resolveUpgrade`
 * is supplied, each item is run through it to get an upgrade preset, which
 * becomes a one-click `UpgradeCTA`. Strengths and Risks stay diagnostic plain
 * text so a concern isn't duplicated as a clickable fix.
 *
 * @param {('up'|'down'|'flat')} tone
 * @param {(item) => ({preset, title}|null)} [resolveUpgrade]  suggestions only
 * @param {string} [origin]  launching-pane id for the CTA's analytics + TCG attribution
 */
export function ProfileMarkerColumn({ tone, title, subtitle, items, emptyText, resolveUpgrade, origin }) {
    return (
        <div className={`profileMarkerColumn profileMarkerColumn--${tone}`}>
            <div className="profileMarkerColumnHeader">
                <span className="profileMarkerColumnArrow" aria-hidden="true">
                    {tone === 'up' ? '▲' : tone === 'down' ? '▼' : '↑'}
                </span>
                <span className="profileMarkerColumnTitle">{title}</span>
                <span className="profileMarkerColumnCount">{items.length}</span>
            </div>
            <span className="profileMarkerColumnSubtitle">{subtitle}</span>
            {items.length === 0 ? (
                <span className="profileMarkerColumnEmpty">{emptyText}</span>
            ) : (
                <ul className="profileMarkerList">
                    {items.map((item) => {
                        const up = tone === 'flat' && resolveUpgrade ? resolveUpgrade(item) : null;
                        return (
                            <li key={item.id} className="profileMarkerListItem">
                                {up ? (
                                    <UpgradeCTA
                                        variant="inline"
                                        label={item.text}
                                        title={up.title}
                                        preset={up.preset}
                                        fallbackRender={item.text}
                                        origin={origin}
                                    />
                                ) : (
                                    item.text
                                )}
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}
